import {Component} from "@angular/core";
import {ListViewItem} from "@domain/common/list-view/list-view-item";
import {IndemnitesRegleAttributionItem} from "@components/admin/frais/indemnites/indemnites-regles-attribution/indemnites-regles-attribution.component";

/**
 * Composant d'affichage d'une règle d'attribution
 */
@Component({
    host: {'data-test-id': 'indemnites-regles-attribution-list-item'},
    selector: 'indemnites-regles-attribution-list-item',
    templateUrl: './indemnites-regles-attribution-list-item.component.html'
})
export class IndemnitesReglesAttributionListItemComponent extends ListViewItem<IndemnitesRegleAttributionItem> {
    /**
     * Consultation de l'élément courant
     */
    consultRegleAttribution(): void {
        //Délégation au composant parent
        this.extraOptions?.consultRegleAttribution?.(this.data);
    }

    /**
     * Suppression de l'élément courant
     */
    deleteRegleAttribution(): void {
        //Délégation au composant parent
        this.extraOptions?.deleteRegleAttribution?.(this.data);
    }

    /**
     * Retourne le mode de gestion à afficher
     */
    getLibelleModeGestion(): string {
        switch (this.data?.modeGestion) {
            case 'UNE_FOIS_PAR_JOUR':
                return 'admin.frais.indemnites.reglesAttribution.liste.modesGestion.uneFoisParJour';
            case 'TRANCHE_HORAIRE':
                return 'admin.frais.indemnites.reglesAttribution.liste.modesGestion.trancheHoraire';
            case 'TEMPS_PASSE':
                return 'admin.frais.indemnites.reglesAttribution.liste.modesGestion.tempsPasse';
            default:
                return this.data?.modeGestion ?? '-';
        }
    }

    /**
     * Indique si la ligne d'information du mode doit être affichée
     */
    isInfosModeGestionVisible(): boolean {
        return this.data?.modeGestion === 'TRANCHE_HORAIRE' || this.data?.modeGestion === 'TEMPS_PASSE';
    }

    /**
     * Retourne le libellé à afficher pour la ligne d'information du mode
     */
    getLibelleInfosModeGestion(): string {
        switch (this.data?.modeGestion) {
            case 'TRANCHE_HORAIRE':
                return 'admin.frais.indemnites.reglesAttribution.liste.modesGestion.trancheHoraire';
            case 'TEMPS_PASSE':
                return 'admin.frais.indemnites.reglesAttribution.liste.modesGestion.tempsPasse';
            default:
                return '';
        }
    }

    /**
     * Retourne les informations relatives au mode de gestion
     */
    getInfosModeGestion(): string {
        //Mode tranche horaire
        if (this.data?.modeGestion === 'TRANCHE_HORAIRE') {
            return `${this.data?.heureDeb || '-'} - ${this.data?.heureFin || '-'}`;
        }

        //Mode temps passé
        if (this.data?.modeGestion === 'TEMPS_PASSE') {
            return `${this.data?.dureeMin ?? 0} - ${this.data?.dureeMax ?? 24}h`;
        }

        //Mode une fois par jour (pas d'information complémentaire)
        return '-';
    }

    /**
     * Indique si le tag domaine doit être affiché
     */
    isTagDomaineVisible(): boolean {
        return (this.data?.nombrePrestations ?? 0) > 0;
    }

    /**
     * Retourne la valeur à afficher dans le tag domaine
     */
    getValeurTagDomaine(): string {
        return this.data?.libelleDomainePrestation || '-';
    }

    /**
     * Retourne la clé de libellé Prestation/Prestations selon le nombre
     */
    getLibellePrestations(): string {
        if ((this.data?.nombrePrestations ?? 0) > 1) {
            return 'admin.frais.indemnites.reglesAttribution.liste.colonnes.prestations';
        }

        return 'admin.frais.indemnites.reglesAttribution.liste.colonnes.prestation';
    }

    /**
     * Indique si le tag prestations doit être affiché
     */
    isTagPrestationsVisible(): boolean {
        return (this.data?.nombrePrestations ?? 0) > 0;
    }

    /**
     * Indique si la règle possède plusieurs prestations
     */
    isPrestationsMultiples(): boolean {
        return (this.data?.nombrePrestations ?? 0) > 1;
    }

    /**
     * Retourne la valeur à afficher dans le tag prestations
     */
    getValeurTagPrestations(): string {
        const nombrePrestations = this.data?.nombrePrestations ?? 0;
        const libelleUniquePrestation = this.getLibelleUniquePrestation();

        //Affichage du nombre à partir de deux prestations
        if (nombrePrestations > 1) {
            return `${nombrePrestations}`;
        }

        //Affichage du libellé unique pour une prestation
        if (nombrePrestations === 1 && libelleUniquePrestation) {
            return libelleUniquePrestation;
        }

        //Fallback défensif
        return `${nombrePrestations}`;
    }

    /**
     * Retourne le contenu du tooltip des prestations liées
     */
    getTooltipPrestations(): string {
        //Le tooltip n'est affiché que pour plusieurs prestations
        if (!this.isPrestationsMultiples()) {
            return '';
        }

        return this.data?.libellesPrestations || '';
    }

    /**
     * Retourne le libellé de la prestation unique
     */
    private getLibelleUniquePrestation(): string {
        const libellesPrestations = this.data?.libellesPrestations || '';
        const listeLibelles = libellesPrestations
            .split('\n')
            .map((libelle: string) => libelle?.trim())
            .filter((libelle: string) => !!libelle);

        return listeLibelles.length > 0 ? listeLibelles[0] : '';
    }
}
